import type { SearchClient } from 'algoliasearch/lite'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef } from 'react'
import { useInstantSearch, useSearchBox, useStats } from 'react-instantsearch'

import logger from '@/client/utils/logger'
import useAiSearchStore from '@/stores/aiSearchStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'
import sessionStore from '@/stores/sessionStore'

import { useAudioSearch } from './audio-search/useAudioSearch'
import { useCoreAutocomplete, useSearchStateSync } from './autocomplete/hooks'
import type { UseAutocompleteProps } from './SearchBox.types'

export const useAutocomplete = (
	searchClient: SearchClient
): UseAutocompleteProps => {
	const user = sessionStore((state) => state.session?.user)
	const { query, refine } = useSearchBox()
	const { nbHits } = useStats()
	const { locale } = useRouter()
	const {
		status,
		setIndexUiState,
		indexUiState: { refinementList },
	} = useInstantSearch()

	const inputRef = useRef<HTMLInputElement>(null)
	const listboxId = 'search-listbox'
	const manualFocusRef = useRef<boolean>(false)

	const {
		prefetchedSearchByAudio,
		setSearchByPrompt,
		clearAiSearch,
		setSearchByAudio,
	} = useAiSearchStore()
	const setInputDraft = useSearchSelectorStore((state) => state.setInputDraft)
	const searchType = useSearchSelectorStore((state) => state.searchType)
	const prevSearchTypeForPanelRef = useRef(searchType)

	const shouldShowEmptyState = useCallback(() => manualFocusRef.current, [])

	// Core autocomplete functionality
	const { autocomplete, autocompleteState } = useCoreAutocomplete({
		searchClient,
		user,
		setSearchByPrompt,
		refine,
		clearAiSearch,
		locale,
		setIndexUiState,
		refinementList,
		setSearchByAudio,
		shouldShowEmptyState,
	})

	// Audio search functionality
	const { handleAudioKeyDown } = useAudioSearch({
		autocomplete,
		autocompleteState,
		user,
		inputRef,
	})

	useEffect(() => {
		const input = inputRef.current
		if (!input) return

		const handleBlur = (event: FocusEvent): void => {
			try {
				// Check if the focus is moving to the dropdown
				const relatedTarget = event.relatedTarget
				const isFocusMovingToDropdown =
					relatedTarget &&
					relatedTarget instanceof HTMLElement &&
					relatedTarget.closest(`#${listboxId}`)

				// Only close if not moving to the dropdown
				if (!isFocusMovingToDropdown) {
					autocomplete.setIsOpen(false)
				}
			} catch (error) {
				logger('Error handling input blur', error, {})
			}
		}

		const handleKeyDown = (event: KeyboardEvent): void => {
			try {
				manualFocusRef.current = true
				handleAudioKeyDown(event)
			} catch (error) {
				logger('Error handling keydown', error, {})
			}
		}

		const handleManualFocus = (): void => {
			manualFocusRef.current = true
		}

		input.addEventListener('input', handleManualFocus)
		input.addEventListener('focus', handleManualFocus)
		input.addEventListener('click', handleManualFocus)
		input.addEventListener('keydown', handleKeyDown)
		input.addEventListener('blur', handleBlur)

		return () => {
			input.removeEventListener('input', handleManualFocus)
			input.removeEventListener('focus', handleManualFocus)
			input.removeEventListener('click', handleManualFocus)
			input.removeEventListener('keydown', handleKeyDown)
			input.removeEventListener('blur', handleBlur)
		}
	}, [autocomplete, inputRef, listboxId, handleAudioKeyDown])

	useEffect(() => {
		const previous = prevSearchTypeForPanelRef.current
		if (previous !== searchType) {
			prevSearchTypeForPanelRef.current = searchType
			autocomplete.setIsOpen(false)
		}
	}, [autocomplete, searchType])

	// State synchronization
	useSearchStateSync({
		autocomplete,
		inputRef,
		query,
		searchType,
		refinementList,
		refine,
		status,
		nbHits,
	})

	const onSearchTypeWillChange = useCallback((): void => {
		const value = inputRef.current?.value ?? ''
		setInputDraft(value)
		refine('')
	}, [refine, setInputDraft])

	return {
		iconName: prefetchedSearchByAudio?.service ?? null,
		inputRef,
		listboxId,
		autocomplete,
		autocompleteState,
		currentQuery: query,
		onSearchTypeWillChange,
	}
}
